<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { onClickOutside } from "@vueuse/core";
import AIMenuPopupItem from "./AIMenuPopupItem.vue";
import FeedbackModal from "@/components/UI/FeedbackModal.vue";
import type { ModularGenerationStatusResponse } from "@/types/ai-generation.types";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const LOG_PREFIX = "[AIMenuPopup]";

const props = defineProps<{
  visible: boolean;
  hasDocument: boolean;
  hasObservations: boolean;
  hasFieldParts: boolean;
  isExtractingData: boolean;
  hasSuggestions: boolean;
  findingsGenerationStatus: ModularGenerationStatusResponse | null;
  isReportGenerating: boolean;
  hasFindings: boolean;
  hasReportImages: boolean;
  isPhotoRenumbering: boolean;
  iqoaAssociationStatus: "idle" | "completed" | "outdated";
  isAssociatingIqoa: boolean;
  isGeneratingConclusions: boolean;
  conclusionsLastCompletedAt: string | Date | null;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "documentExtraction"): void;
  (e: "findingsGeneration"): void;
  (e: "photoRenumbering"): void;
  (e: "iqoaAssociation"): void;
  (e: "conclusionsGeneration"): void;
}>();

const { t } = useTranslation();

// Log props when popup becomes visible
watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      console.log(`${LOG_PREFIX} Popup visible - Props received:`, {
        hasDocument: props.hasDocument,
        hasObservations: props.hasObservations,
        isExtractingData: props.isExtractingData,
        hasSuggestions: props.hasSuggestions,
        findingsGenerationStatus: props.findingsGenerationStatus,
        isReportGenerating: props.isReportGenerating,
        hasFindings: props.hasFindings,
        hasReportImages: props.hasReportImages,
        isPhotoRenumbering: props.isPhotoRenumbering,
        iqoaAssociationStatus: props.iqoaAssociationStatus,
        isAssociatingIqoa: props.isAssociatingIqoa,
        isGeneratingConclusions: props.isGeneratingConclusions,
        conclusionsLastCompletedAt: props.conclusionsLastCompletedAt,
      });
    }
  },
);

const popupRef = ref<HTMLElement | null>(null);

onClickOutside(
  popupRef,
  () => {
    if (props.visible) {
      emit("close");
    }
  },
  { ignore: [".ai-fab-button"] },
);

// Document extraction status
const documentExtractionStatus = computed(() => {
  if (!props.hasDocument) return "idle";
  if (props.hasSuggestions) return "completed";
  return "idle";
});

// Disable only if no document AND not currently extracting
// (if extracting, user can click to re-open overlay)
// Also disable if another module is processing
const isDocumentExtractionDisabled = computed(() => {
  // If this module is extracting, allow click to reopen overlay
  if (props.isExtractingData) return false;
  // Disable if no document or if another module is processing
  return !props.hasDocument || isAnyModuleProcessing.value;
});

// Findings generation status
const findingsGenerationStatus = computed(() => {
  const reportGenModule =
    props.findingsGenerationStatus?.modules?.report_generation;
  if (!reportGenModule) return "idle";
  if (reportGenModule.status === "completed") return "completed";
  if (reportGenModule.status === "outdated") return "outdated";
  return "idle";
});

const isFindingsGenerating = computed(() => props.isReportGenerating);
const isIQOALoading = computed(() => props.isAssociatingIqoa);
const iqoaStatus = computed(() => props.iqoaAssociationStatus);

// Check if any module is currently processing
const isAnyModuleProcessing = computed(
  () =>
    props.isExtractingData ||
    props.isReportGenerating ||
    props.isAssociatingIqoa ||
    props.isPhotoRenumbering ||
    props.isGeneratingConclusions,
);

// Disable only if no observations AND no field parts AND not currently generating
// (if generating, user can click to re-open overlay)
// Also disable if another module is processing
const isFindingsDisabled = computed(() => {
  // If this module is generating, allow click to reopen overlay
  if (props.isReportGenerating) return false;
  // Disable if no observations and no field parts, or if another module is processing
  return (
    (!props.hasObservations && !props.hasFieldParts) ||
    isAnyModuleProcessing.value
  );
});

const handleDocumentExtraction = () => {
  console.log(`${LOG_PREFIX} handleDocumentExtraction clicked`);

  if (!isDocumentExtractionDisabled.value) {
    console.log(`${LOG_PREFIX} Emitting documentExtraction event`);
    emit("documentExtraction");
  }
};

const handleFindingsGeneration = () => {
  console.log(`${LOG_PREFIX} handleFindingsGeneration clicked`);

  if (!isFindingsDisabled.value) {
    console.log(`${LOG_PREFIX} Emitting findingsGeneration event`);
    emit("findingsGeneration");
  }
};

// Photo renumbering
const isPhotoRenumberingDisabled = computed(() => {
  if (props.isPhotoRenumbering) return false;
  return !props.hasReportImages || isAnyModuleProcessing.value;
});

const handlePhotoRenumbering = () => {
  console.log(`${LOG_PREFIX} handlePhotoRenumbering clicked`);

  if (!isPhotoRenumberingDisabled.value) {
    console.log(`${LOG_PREFIX} Emitting photoRenumbering event`);
    emit("photoRenumbering");
  }
};

// IQOA association
// Disable only if no findings AND not currently associating
// Also disable if another module is processing
const isIQOADisabled = computed(() => {
  // If this module is associating, allow click to reopen overlay
  if (isIQOALoading.value) return false;
  // Disable if no findings or if another module is processing
  return !props.hasFindings || isAnyModuleProcessing.value;
});

const handleIQOAAssociation = () => {
  console.log(`${LOG_PREFIX} handleIQOAAssociation clicked`);

  if (!isIQOADisabled.value) {
    console.log(`${LOG_PREFIX} Emitting iqoaAssociation event`);
    emit("iqoaAssociation");
  }
};

// Conclusions generation
const conclusionsStatus = computed<"idle" | "completed">(() =>
  props.conclusionsLastCompletedAt ? "completed" : "idle",
);

const isConclusionsDisabled = computed(() => {
  // If currently generating, allow click to reopen overlay
  if (props.isGeneratingConclusions) return false;
  // Disable if no findings or another module is processing
  return !props.hasFindings || isAnyModuleProcessing.value;
});

const handleConclusionsGeneration = () => {
  console.log(`${LOG_PREFIX} handleConclusionsGeneration clicked`);

  if (!isConclusionsDisabled.value) {
    console.log(`${LOG_PREFIX} Emitting conclusionsGeneration event`);
    emit("conclusionsGeneration");
  }
};

// Feedback modal
const showFeedbackModal = ref(false);

const handleFeedback = () => {
  showFeedbackModal.value = true;
};
</script>

<template>
  <Transition name="popup">
    <div v-if="visible" ref="popupRef" class="ai-menu-popup">
      <!-- Header -->
      <div class="popup-header">
        <i class="bi-stars" />
        <span>{{ t("report:ai.menu.title") }}</span>
      </div>

      <!-- Menu items -->
      <div class="popup-content">
        <!-- Step 1: Document extraction -->
        <AIMenuPopupItem
          icon="bi-file-earmark-text"
          :label="t('report:ai.menu.documentExtraction.label')"
          :description="t('report:ai.menu.documentExtraction.description')"
          :status="documentExtractionStatus"
          :disabled="isDocumentExtractionDisabled"
          :loading="isExtractingData"
          @click="handleDocumentExtraction"
        />

        <!-- Step 2: Findings generation -->
        <AIMenuPopupItem
          icon="bi-list-nested"
          :label="t('report:ai.menu.findingsGeneration.label')"
          :description="t('report:ai.menu.findingsGeneration.description')"
          :status="findingsGenerationStatus"
          :disabled="isFindingsDisabled"
          :loading="isFindingsGenerating"
          @click="handleFindingsGeneration"
        />

        <!-- Step 3: Photo renumbering -->
        <AIMenuPopupItem
          icon="bi-123"
          :label="t('report:ai.menu.photoRenumbering.label')"
          :description="t('report:ai.menu.photoRenumbering.description')"
          :disabled="isPhotoRenumberingDisabled"
          :loading="isPhotoRenumbering"
          @click="handlePhotoRenumbering"
        />

        <!-- Step 4: IQOA Catalog -->
        <AIMenuPopupItem
          icon="bi-book"
          :label="t('report:ai.menu.iqoaCatalog.label')"
          :description="t('report:ai.menu.iqoaCatalog.description')"
          :status="iqoaStatus"
          :disabled="isIQOADisabled"
          :loading="isIQOALoading"
          @click="handleIQOAAssociation"
        />

        <!-- Step 5: Conclusions generation -->
        <AIMenuPopupItem
          icon="bi-card-text"
          :label="t('report:ai.menu.conclusions.label')"
          :description="t('report:ai.menu.conclusions.description')"
          :status="conclusionsStatus"
          :disabled="isConclusionsDisabled"
          :loading="isGeneratingConclusions"
          @click="handleConclusionsGeneration"
        />

        <!-- Feedback link (discreet) -->
        <div class="feedback-link" @click="handleFeedback">
          <PhosphorIcon name="chat-dots" :size="18" />
          <span>{{ t("feedback.bot.button") }}</span>
        </div>
      </div>
    </div>
  </Transition>

  <FeedbackModal v-model="showFeedbackModal" view="Report" />
</template>

<style scoped lang="scss">
.ai-menu-popup {
  position: fixed;
  bottom: 90px;
  right: 24px;
  width: 340px;
  max-width: calc(100vw - 48px);
  background: var(--el-bg-color);
  border-radius: 16px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.15),
    0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  overflow: hidden;
  border: 1px solid var(--el-border-color-lighter);
}

.popup-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: white;
  font-weight: 600;
  font-size: 16px;

  i {
    font-size: 20px;
  }
}

.popup-content {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.feedback-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  cursor: pointer;
  border-top: 1px solid var(--el-border-color-lighter);

  &:hover {
    color: var(--el-text-color-primary);
  }

  i {
    font-size: 12px;
  }
}

// Transition
.popup-enter-active,
.popup-leave-active {
  transition: all 0.2s ease;
}

.popup-enter-from,
.popup-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}
</style>
